import * as React from "react"

const isBrowser = typeof window !== "undefined"

// Client nur im Browser initialisieren - verhindert SSR Probleme
let client = null

const initClient = async () => {
    if (!isBrowser || client) return client

    const { createStorefrontApiClient } = await import("@shopify/storefront-api-client")
    client = createStorefrontApiClient({
        storeDomain: `https://${process.env.GATSBY_SHOPIFY_STORE_URL}`,
        apiVersion: "2025-01",
        publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
    })
    return client
}

// GraphQL Fragments
const CART_FRAGMENT = `
  fragment CartFragment on Cart {
    id
    checkoutUrl
    totalQuantity
    cost {
      subtotalAmount {
        amount
        currencyCode
      }
      totalAmount {
        amount
        currencyCode
      }
      totalTaxAmount {
        amount
        currencyCode
      }
    }
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          cost {
            totalAmount {
              amount
              currencyCode
            }
          }
          merchandise {
            ... on ProductVariant {
              id
              title
              image {
                url
                altText
                width
                height
              }
              price {
                amount
                currencyCode
              }
              product {
                title
                handle
              }
              selectedOptions {
                name
                value
              }
            }
          }
        }
      }
    }
  }
`

// GraphQL Mutations & Queries
const CREATE_CART_MUTATION = `
  mutation cartCreate($input: CartInput, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    cartCreate(input: $input) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`

const GET_CART_QUERY = `
  query getCart($cartId: ID!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    cart(id: $cartId) {
      ...CartFragment
    }
  }
  ${CART_FRAGMENT}
`

const ADD_LINES_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`

const UPDATE_LINES_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`

const REMOVE_LINES_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ...CartFragment
      }
      userErrors {
        field
        message
      }
    }
  }
  ${CART_FRAGMENT}
`

// Helper to transform cart lines to a more usable format
const transformCartLines = (cart) => {
    if (!cart?.lines?.edges) return []

    return cart.lines.edges.map(({ node }) => ({
        id: node.id,
        quantity: node.quantity,
        title: node.merchandise?.product?.title,
        variant: {
            id: node.merchandise?.id,
            title: node.merchandise?.title,
            image: node.merchandise?.image,
            price: node.merchandise?.price,
            product: node.merchandise?.product,
            selectedOptions: node.merchandise?.selectedOptions,
        },
        cost: node.cost,
    }))
}

// Default context values
const defaultValues = {
    cart: null,
    lineItems: [],
    isOpen: false,
    loading: false,
    didJustAddToCart: false,
    onOpen: () => {},
    onClose: () => {},
    addVariantToCart: () => {},
    removeLineItem: () => {},
    updateLineItem: () => {},
    client: null,
    checkout: {
        lineItems: [],
        webUrl: "",
        subtotalPrice: { amount: "0", currencyCode: "EUR" },
        totalPrice: { amount: "0", currencyCode: "EUR" },
    },
}

export const StoreContext = React.createContext(defaultValues)

const localStorageKey = "shopify_cart_id"

// Context for internationalization
const CONTEXT = {
    country: "DE",
    language: "DE",
}

export const StoreProvider = ({ children }) => {
    const [cart, setCart] = React.useState(null)
    const [loading, setLoading] = React.useState(false)
    const [didJustAddToCart, setDidJustAddToCart] = React.useState(false)

    // Save cart ID to localStorage
    const setCartId = (cartId) => {
        if (isBrowser) {
            localStorage.setItem(localStorageKey, cartId)
        }
    }

    // Get cart ID from localStorage
    const getCartId = () => {
        if (isBrowser) {
            return localStorage.getItem(localStorageKey)
        }
        return null
    }

    // Create a new cart
    const createCart = async () => {
        if (!isBrowser) return null

        try {
            const apiClient = await initClient()
            if (!apiClient) return null

            const { data, errors } = await apiClient.request(CREATE_CART_MUTATION, {
                variables: {
                    input: {},
                    ...CONTEXT,
                },
            })

            if (errors) {
                console.error("Error creating cart:", errors)
                return null
            }

            if (data?.cartCreate?.userErrors?.length > 0) {
                console.error("Cart creation errors:", data.cartCreate.userErrors)
                return null
            }

            const newCart = data?.cartCreate?.cart
            if (newCart) {
                setCartId(newCart.id)
                setCart(newCart)
            }
            return newCart
        } catch (error) {
            console.error("Failed to create cart:", error)
            return null
        }
    }

    // Fetch existing cart
    const fetchCart = async (cartId) => {
        if (!isBrowser) return null

        try {
            const apiClient = await initClient()
            if (!apiClient) return null

            const { data, errors } = await apiClient.request(GET_CART_QUERY, {
                variables: {
                    cartId,
                    ...CONTEXT,
                },
            })

            if (errors) {
                console.error("Error fetching cart:", errors)
                return null
            }

            return data?.cart
        } catch (error) {
            console.error("Failed to fetch cart:", error)
            return null
        }
    }

    // Initialize cart on mount
    React.useEffect(() => {
        if (!isBrowser) return

        const initializeCart = async () => {
            const existingCartId = getCartId()

            if (existingCartId && existingCartId !== "null") {
                try {
                    const existingCart = await fetchCart(existingCartId)

                    if (existingCart) {
                        setCart(existingCart)
                        return
                    }
                } catch (e) {
                    console.error("Error fetching existing cart:", e)
                    localStorage.removeItem(localStorageKey)
                }
            }

            await createCart()
        }

        initializeCart()
    }, [])

    // Add variant to cart
    const addVariantToCart = async (variantId, quantity) => {
        if (!isBrowser) return

        setLoading(true)

        try {
            const apiClient = await initClient()
            if (!apiClient) {
                setLoading(false)
                return
            }

            let cartId = cart?.id

            if (!cartId) {
                const newCart = await createCart()
                cartId = newCart?.id
            }

            if (!cartId) {
                console.error("No cart available")
                setLoading(false)
                return
            }

            const { data, errors } = await apiClient.request(ADD_LINES_MUTATION, {
                variables: {
                    cartId,
                    lines: [
                        {
                            merchandiseId: variantId,
                            quantity: parseInt(quantity, 10),
                        },
                    ],
                    ...CONTEXT,
                },
            })

            if (errors) {
                console.error("Error adding to cart:", errors)
                setLoading(false)
                return
            }

            if (data?.cartLinesAdd?.userErrors?.length > 0) {
                console.error("Add to cart errors:", data.cartLinesAdd.userErrors)
                setLoading(false)
                return
            }

            const updatedCart = data?.cartLinesAdd?.cart
            if (updatedCart) {
                setCartId(updatedCart.id)
                setCart(updatedCart)
                console.log("Added to cart:", updatedCart)
            }

            setLoading(false)
            setDidJustAddToCart(true)
            setTimeout(() => setDidJustAddToCart(false), 3000)
        } catch (error) {
            console.error("Failed to add to cart:", error)
            setLoading(false)
        }
    }

    // Remove line item from cart
    const removeLineItem = async (cartId, lineItemId) => {
        if (!isBrowser) return

        setLoading(true)

        try {
            const apiClient = await initClient()
            if (!apiClient) {
                setLoading(false)
                return
            }

            const { data, errors } = await apiClient.request(REMOVE_LINES_MUTATION, {
                variables: {
                    cartId: cartId || cart?.id,
                    lineIds: [lineItemId],
                    ...CONTEXT,
                },
            })

            if (errors) {
                console.error("Error removing from cart:", errors)
                setLoading(false)
                return
            }

            if (data?.cartLinesRemove?.userErrors?.length > 0) {
                console.error("Remove from cart errors:", data.cartLinesRemove.userErrors)
                setLoading(false)
                return
            }

            const updatedCart = data?.cartLinesRemove?.cart
            if (updatedCart) {
                setCartId(updatedCart.id)
                setCart(updatedCart)
            }

            setLoading(false)
        } catch (error) {
            console.error("Failed to remove from cart:", error)
            setLoading(false)
        }
    }

    // Update line item quantity
    const updateLineItem = async (cartId, lineItemId, quantity) => {
        if (!isBrowser) return

        setLoading(true)

        try {
            const apiClient = await initClient()
            if (!apiClient) {
                setLoading(false)
                return
            }

            const { data, errors } = await apiClient.request(UPDATE_LINES_MUTATION, {
                variables: {
                    cartId: cartId || cart?.id,
                    lines: [
                        {
                            id: lineItemId,
                            quantity: parseInt(quantity, 10),
                        },
                    ],
                    ...CONTEXT,
                },
            })

            if (errors) {
                console.error("Error updating cart:", errors)
                setLoading(false)
                return
            }

            if (data?.cartLinesUpdate?.userErrors?.length > 0) {
                console.error("Update cart errors:", data.cartLinesUpdate.userErrors)
                setLoading(false)
                return
            }

            const updatedCart = data?.cartLinesUpdate?.cart
            if (updatedCart) {
                setCartId(updatedCart.id)
                setCart(updatedCart)
            }

            setLoading(false)
        } catch (error) {
            console.error("Failed to update cart:", error)
            setLoading(false)
        }
    }

    // Transform cart to backwards-compatible checkout format
    const lineItems = transformCartLines(cart)

    const checkout = {
        id: cart?.id,
        webUrl: cart?.checkoutUrl || "",
        lineItems,
        subtotalPrice: cart?.cost?.subtotalAmount || { amount: "0", currencyCode: "EUR" },
        totalPrice: cart?.cost?.totalAmount || { amount: "0", currencyCode: "EUR" },
        totalTax: cart?.cost?.totalTaxAmount,
        totalQuantity: cart?.totalQuantity || 0,
        completedAt: null,
    }

    return (
        <StoreContext.Provider
            value={{
                ...defaultValues,
                cart,
                checkout,
                lineItems,
                addVariantToCart,
                removeLineItem,
                updateLineItem,
                loading,
                didJustAddToCart,
            }}
        >
            {children}
        </StoreContext.Provider>
    )
}

// Custom hook for easy access
export const useStore = () => {
    const context = React.useContext(StoreContext)
    if (context === undefined) {
        throw new Error("useStore must be used within a StoreProvider")
    }
    return context
}